import * as Yup from "yup";

import {
  GenerateFinanceTreatyReport,
  GenerateFinanceTreatyReportVariables,
} from "_graphql/mutation/kek/__generated__/GenerateFinanceTreatyReport";
import {
  InsurerPicker,
  OfficeButton,
  ReInsurerPicker,
  SelectInput,
  Show,
  TableComponent,
  TextInput,
  TreatyProgramPicker,
  TreatyTypePicker,
} from "components";
import {
  facultativeReportTypeOptions,
  insurerTable,
  reinsurerTable,
  wrapClick,
} from "utils";

import { Payment_Type } from "_graphql/__generated__/globalTypes-kek";
import { generateFinanceTreatyReport } from "_graphql/kek/mutation/index.kek";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";

type Props = {};

const initialValues = {
  report_type: "",
  client_type: "",
  insurer_id: "",
  payment_type: "",
  treaty_type: "",
  treaty_program: {} as any,
  from: "",
  to: "",
};

const validationSchema = Yup.object().shape({
  report_type: Yup.string().required("Report type is required"),
  client_type: Yup.string().required("Client type is required"),
  insurer_id: Yup.string().required("Insurer is required"),
  payment_type: Yup.string().required("Payment type is required"),
  treaty_type: Yup.string().required("Treaty type is required"),
  treaty_program: Yup.object().required("Treaty program is required"),
  from: Yup.string().required("From date is required"),
  to: Yup.string().required("To date is required"),
});

const Treaty = (props: Props) => {
  const [generate, { loading, data }] = useMutation<
    GenerateFinanceTreatyReport,
    GenerateFinanceTreatyReportVariables
  >(generateFinanceTreatyReport, {
    onCompleted: (data) => {},
    onError: (error) => {
      console.error("Error generating report:", error);
      toast.error("Error generating report");
    },
  });

  const form = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      form.setFormikState((state) => ({ ...state, status: "SUBMITTED" }));
      form.setSubmitting(false);
      await generate({
        variables: {
          reportData: {
            report_type: values.report_type,
            client_type: values.client_type,
            payment_type: values.payment_type,
            treaty_type: values.treaty_type,
            period_from: values.from,
            period_to: values.to,
            treaty_program_id: values.treaty_program?.id,
            client_id: values.insurer_id,
          },
        },
      });
      return;
    },
  });

  return (
    <div className="w-full">
      <div className="py-3 flex justify-between">
        <div>
          <h1 className="font-bold text-primary text-lg">Treaty</h1>
        </div>
        <div className="flex items-center space-x-2">
          <OfficeButton
            disabled={loading}
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Generating ..." : "Generate Report"}
          </OfficeButton>
        </div>
      </div>
      <form className="space-y-6 divide-y shadow-sm rounded-md bg-white p-6 divide-gray-200">
        <div>
          <span className="text-xs font-light">Treaty Parameters</span>
          <div className="">
            <SelectInput
              id={"report_type"}
              label={"Report Type"}
              options={facultativeReportTypeOptions}
              {...form}
            />
          </div>
          <div className="grid grid-cols-2 gap-5 my-5">
            <div>
              <SelectInput
                id={"client_type"}
                label={"Client Type"}
                options={[
                  { label: "Reinsurer", value: "REINSURER" },
                  { label: "Insurer", value: "INSURER" },
                  {
                    label: "Retrocedent",
                    value: "RETROCEDENT",
                  },
                ]}
                {...form}
              />
            </div>
            <div>
              {form.values.client_type === "INSURER" ? (
                <InsurerPicker
                  id="insurer_id"
                  label="Insurer"
                  placeholder="e.g. Select Insurer"
                  required={true}
                  disabled={form.values.client_type !== "INSURER"}
                  rawId
                  {...form}
                />
              ) : (
                <ReInsurerPicker
                  id="insurer_id"
                  label="Reinsurer"
                  placeholder="e.g. Select Reinsurer"
                  required={true}
                  disabled={
                    form.values.client_type !== "REINSURER" ||
                    form.values.client_type !== "RETROCEDENT"
                  }
                  rawId
                  {...form}
                />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5 mb-5">
            <div className="">
              <SelectInput
                options={[
                  {
                    label: "--- Select ---",
                    value: "",
                  },
                  {
                    label: "Bank Transfer",
                    value: Payment_Type.BANKTRANSFER,
                  },
                  {
                    label: "Cheque",
                    value: Payment_Type.CHEQUE,
                  },
                ]}
                id="payment_type"
                label="Payment Type"
                placeholder="e.g. Select Payment"
                required={true}
                {...form}
              />
            </div>
            <div>
              <TreatyTypePicker
                id="treaty_type"
                label="Treaty Type"
                placeholder="Treaty Type"
                required={true}
                {...form}
              />
            </div>
          </div>
          <div className="">
            <TreatyProgramPicker
              id="treaty_program"
              label="Treaty Program"
              placeholder="Treaty Program"
              // filter={{
              //     type: form.values?.treaty_type as any,
              //     insurer_id: form.values?.insurer_id || "",
              // }}
              required={true}
              {...form}
            />
          </div>
        </div>

        <div className="pt-6">
          <span className="text-xs font-light">Query Date Range</span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <TextInput
                id="from"
                label={form.values.queryOnPayment ? "Payment from" : "From"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
            <div>
              <TextInput
                id="to"
                label={form.values.queryOnPayment ? "Payment to" : "To"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
      </form>
      <Show if={Boolean(form.status === "SUBMITTED" && form.isValid)}>
        <div className="w-full my-5">
          <TableComponent
            title="Treaty Report"
            loading={loading}
            refetch={() => {}}
            data={JSON.parse(data?.generateFinanceTreatyReport ?? "[]")}
            columns={
              ["REINSURER", "RETROCEDENT"].includes(form.values.client_type)
                ? reinsurerTable
                : insurerTable
            }
          />
        </div>
      </Show>
    </div>
  );
};

export default Treaty;
